import React, { Fragment, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../../layout/breadcrumb";
import { Button, Card, CardBody, Col, Container, Input, Row, Table } from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
    aiSuggestCategoryMappingApi,
    getCategoryMappingsApi,
    getShopifyCategoriesApi,
    saveCategoryMappingsApi,
    syncShopifyTaxonomyApi,
} from "../../../../services/manage-product/setting/shopifyCategoryMappingService";

const dataMenu = [
    { type: 0, text: "Products", link: "/product" },
    { type: 1, text: "Product Setting", link: "" },
    { type: 1, text: "Shopify Category Mapping", link: "" },
];

const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");

const statusOptions = [
    { value: "all", label: "All categories" },
    { value: "mapped", label: "Mapped only" },
    { value: "unmapped", label: "Unmapped only" },
];

const ShopifyCategoryMapping = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [defaultShopifyOptions, setDefaultShopifyOptions] = useState([]);
    const [taxonomyCount, setTaxonomyCount] = useState(0);
    const [mapping, setMapping] = useState({});      // { [id]: {value,label} | null }
    const [aiInfo, setAiInfo] = useState({});        // { [id]: {confidence, reason} }
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [{ response: mapRes }, { response: shopRes }] = await Promise.all([
                getCategoryMappingsApi(),
                getShopifyCategoriesApi({}), // small starter list for the dropdowns
            ]);
            const cats = mapRes.categories || [];
            setCategories(cats);
            setTaxonomyCount(mapRes.taxonomy_synced || 0);
            setDefaultShopifyOptions(shopRes.categories || []);

            const seed = {};
            const put = (c) => {
                seed[c.id] = c.shopify_category_gid
                    ? { value: c.shopify_category_gid, label: c.shopify_category_full_name || c.shopify_category_gid }
                    : null;
            };
            cats.forEach((c) => {
                put(c);
                (c.children || []).forEach(put);
            });
            setMapping(seed);
        } catch (e) {
            console.error("Category mapping load failed", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Server-side search across the WHOLE taxonomy (14k+), so any vertical is findable.
    const loadShopifyOptions = async (inputValue) => {
        try {
            const { response } = await getShopifyCategoriesApi(inputValue ? { search: inputValue } : {});
            return response.categories || [];
        } catch (e) {
            return [];
        }
    };

    // Flatten parent -> children into ordered rows for filtering/rendering.
    const flatRows = useMemo(() => {
        const rows = [];
        categories.forEach((c) => {
            rows.push({ id: c.id, name: c.name, isChild: false });
            (c.children || []).forEach((sub) => rows.push({ id: sub.id, name: sub.name, isChild: true }));
        });
        return rows;
    }, [categories]);

    const filteredRows = useMemo(() => {
        const q = norm(search);
        const status = statusFilter?.value || "all";
        return flatRows.filter((r) => {
            if (q && !norm(r.name).includes(q)) return false;
            if (status === "mapped" && !mapping[r.id]) return false;
            if (status === "unmapped" && mapping[r.id]) return false;
            return true;
        });
    }, [flatRows, search, statusFilter, mapping]);

    const handleSelect = (id, option) => setMapping((prev) => ({ ...prev, [id]: option || null }));

    const handleAutoMatchAi = async () => {
        setAiLoading(true);
        try {
            const { response } = await aiSuggestCategoryMappingApi();
            const suggestions = response.suggestions || [];
            const nextMap = { ...mapping };
            const nextAi = { ...aiInfo };
            let filled = 0;
            suggestions.forEach((s) => {
                if (s.shopify_category_gid) {
                    nextMap[s.id] = { value: s.shopify_category_gid, label: s.shopify_category_full_name };
                    filled++;
                }
                nextAi[s.id] = { confidence: s.confidence, reason: s.reason };
            });
            setMapping(nextMap);
            setAiInfo(nextAi);
            toast.success(`AI suggested ${filled} categories — review confidence/reason, then Save.`);
        } catch (e) {
            toast.error("AI match failed");
        }
        setAiLoading(false);
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { response } = await syncShopifyTaxonomyApi();
            toast.success(`Synced ${response.fetched} Shopify categories.`);
            await loadData();
        } catch (e) {
            toast.error("Taxonomy sync failed");
        }
        setSyncing(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const rows = [];
            const collect = (c) => {
                const sel = mapping[c.id] || null;
                rows.push({
                    id: c.id,
                    shopify_category_gid: sel ? sel.value : null,
                    shopify_category_full_name: sel ? sel.label : null,
                });
            };
            categories.forEach((c) => {
                collect(c);
                (c.children || []).forEach(collect);
            });
            const { response } = await saveCategoryMappingsApi({ mappings: rows });
            toast.success(`Saved ${response.saved} mappings.`);
        } catch (e) {
            toast.error("Failed to save mappings");
        }
        setSaving(false);
    };

    const confidenceColor = (c) => (c >= 80 ? "#28a745" : c >= 50 ? "#e0a800" : "#dc3545");
    const showTools = taxonomyCount > 0 && categories.length > 0;

    const renderRow = (r) => {
        const ai = aiInfo[r.id];
        return (
            <tr key={r.id}>
                <td style={{ paddingLeft: r.isChild ? 34 : 14, fontWeight: r.isChild ? 400 : 600, verticalAlign: "middle", width: "34%" }}>
                    {r.isChild ? "↳ " : ""}
                    {r.name}
                </td>
                <td>
                    <AsyncSelect
                        isClearable
                        cacheOptions
                        defaultOptions={defaultShopifyOptions}
                        loadOptions={loadShopifyOptions}
                        placeholder="Search Shopify category…"
                        value={mapping[r.id] || null}
                        onChange={(opt) => handleSelect(r.id, opt)}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        noOptionsMessage={({ inputValue }) => (inputValue ? "No matching Shopify category" : "Type to search…")}
                    />
                    {ai && (ai.confidence != null || ai.reason) && (
                        <div className="mt-1" style={{ fontSize: 12 }}>
                            {ai.confidence != null && (
                                <span style={{ color: confidenceColor(ai.confidence), fontWeight: 600 }}>
                                    AI confidence: {ai.confidence}%
                                </span>
                            )}
                            {ai.reason && <span className="text-muted"> — {ai.reason}</span>}
                        </div>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                <Breadcrumb data={dataMenu} title="Shopify Category Mapping" />
                <div className="product-grid">
                    <div className="feature-products">
                        {/* Header: title + actions */}
                        <Card>
                            <Row className="p-3 align-items-center">
                                <Col md="6">
                                    <h5 className="mb-1">Map our categories to Shopify's Standard Product Taxonomy</h5>
                                    <small className="text-muted">
                                        Shopify categories cached locally: <b>{taxonomyCount}</b>
                                        {taxonomyCount === 0 && " — click \"Sync Shopify Categories\" first."}
                                    </small>
                                </Col>
                                <Col md="6">
                                    <div className="float-md-right mt-2 mt-md-0">
                                        <Button size="sm" color="secondary" outline className="mr-2" disabled={syncing} onClick={handleSync}>
                                            {syncing ? "Syncing…" : taxonomyCount > 0 ? "Re-sync Shopify Categories" : "Sync Shopify Categories"}
                                        </Button>
                                        {showTools && (
                                            <Fragment>
                                                <Button size="sm" color="primary" outline className="mr-2" disabled={aiLoading} onClick={handleAutoMatchAi}>
                                                    {aiLoading ? "AI matching…" : "Auto-match by AI"}
                                                </Button>
                                                <Button size="sm" color="primary" disabled={saving} onClick={handleSave}>
                                                    {saving ? "Saving…" : "Save Mappings"}
                                                </Button>
                                            </Fragment>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* Filters + table (own white card) */}
                        <Card>
                            <CardBody>
                                {showTools && (
                                    <Row className="mb-1">
                                        <Col md="6" className="mb-2">
                                            <Input
                                                type="text"
                                                placeholder="Search by category in marketplace…"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                        </Col>
                                        <Col md="4" className="mb-2">
                                            <Select
                                                options={statusOptions}
                                                value={statusFilter}
                                                onChange={(opt) => setStatusFilter(opt || statusOptions[0])}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                {loading ? (
                                    <PreLoader />
                                ) : (
                                    <div className="table-responsive product-table mt-2">
                                        <Table className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Our Category</th>
                                                    <th>Shopify Category (Standard Product Taxonomy)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.map(renderRow)}
                                                {!filteredRows.length && (
                                                    <tr>
                                                        <td colSpan="2" className="text-center text-muted py-4">
                                                            {categories.length ? "No categories match your filters." : "No categories found."}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
};

export default ShopifyCategoryMapping;
